import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Table, Button, Tag, Alert, List, Typography, message, Row, Col, Input, Select } from 'antd';
import { ReloadOutlined, ClearOutlined, SearchOutlined } from '@ant-design/icons';

import { avaliacoesAdminService } from '../../services/api';
import { buildApiErrorMessage } from '../../utils/apiError';

const { Text } = Typography;

const FILTROS_INICIAIS = { turma: '', modalidade: null, equipe: null, situacao: null, inconsistencias: null };

// Contrato real: GET /api/avaliacoes/periodos/{id}/turmas
// (TurmaCandidataPeriodoResponse) -- estritamente read-only, nunca
// materializa sozinho. "Materializar" é termo técnico -- na UI usamos
// "Adicionar ao período" / "Incluída" (ver avaliacao_turmas_candidatas_service.py
// para a regra real de candidatura, não repetida aqui).
//
// Filtros/ordenação (ajuste de usabilidade admin) são inteiramente
// client-side: o endpoint já devolve o array completo de turmas
// candidatas do período, sem paginação backend -- nenhum endpoint novo.
const PeriodoTurmasPainel = ({ periodo, onTurmaIncluida }) => {
  const [turmas, setTurmas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [selecionadas, setSelecionadas] = useState([]);
  const [incluindoIds, setIncluindoIds] = useState(new Set()); // turma_id em processamento (unitário)
  const [incluindoLote, setIncluindoLote] = useState(false);
  const [resultadoLote, setResultadoLote] = useState(null); // { sucesso, falha, itensFalha } | null
  const [filtros, setFiltros] = useState(FILTROS_INICIAIS);

  const periodoAberto = periodo.status === 'ABERTO';

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const response = await avaliacoesAdminService.listarTurmasPeriodo(periodo.id);
      setTurmas(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      const { message: mensagem } = buildApiErrorMessage(err, 'Período ou turma não encontrado.');
      setErro(mensagem);
      setTurmas([]);
    } finally {
      setLoading(false);
    }
  }, [periodo.id]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const adicionarUma = async (turma) => {
    setIncluindoIds((atual) => new Set(atual).add(turma.id_turma));
    try {
      await avaliacoesAdminService.materializarTurma(periodo.id, turma.id_turma);
      message.success(`Turma "${turma.nome_turma}" adicionada ao período.`);
      carregar();
      onTurmaIncluida?.();
    } catch (err) {
      const { message: mensagem } = buildApiErrorMessage(err, 'Não foi possível adicionar a turma.');
      message.error(mensagem);
    } finally {
      setIncluindoIds((atual) => {
        const novo = new Set(atual);
        novo.delete(turma.id_turma);
        return novo;
      });
    }
  };

  const adicionarSelecionadas = async () => {
    if (selecionadas.length === 0) return;
    setIncluindoLote(true);
    setResultadoLote(null);
    try {
      const response = await avaliacoesAdminService.materializarTurmas(periodo.id, selecionadas);
      const itens = response.data.itens || [];
      const sucesso = itens.filter((i) => i.sucesso);
      const falha = itens.filter((i) => !i.sucesso);
      setResultadoLote({
        sucesso: sucesso.length,
        falha: falha.length,
        itensFalha: falha.map((i) => ({
          turma_id: i.turma_id,
          nome: turmas.find((t) => t.id_turma === i.turma_id)?.nome_turma || `Turma #${i.turma_id}`,
          mensagem: i.erro_mensagem,
        })),
      });
      setSelecionadas([]);
      carregar();
      onTurmaIncluida?.();
    } catch (err) {
      const { message: mensagem } = buildApiErrorMessage(err, 'Não foi possível adicionar as turmas selecionadas.');
      message.error(mensagem);
    } finally {
      setIncluindoLote(false);
    }
  };

  const handleFiltroChange = (campo, valor) => {
    setFiltros((atual) => ({ ...atual, [campo]: valor }));
  };

  const filtroAtivo =
    filtros.turma.trim() !== '' ||
    filtros.modalidade != null ||
    filtros.equipe != null ||
    filtros.situacao != null ||
    filtros.inconsistencias != null;

  const limparFiltros = () => setFiltros(FILTROS_INICIAIS);

  // Opções dos Selects: derivadas do próprio payload já carregado (sem
  // duplicatas), nunca uma nova chamada ao backend.
  const opcoesModalidade = useMemo(
    () => Array.from(new Set(turmas.map((t) => t.nome_modalidade).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [turmas],
  );
  const opcoesProfessor = useMemo(
    () => Array.from(new Set(turmas.map((t) => t.nome_equipe).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [turmas],
  );

  // Filtragem: sempre produz um array NOVO (nunca muta `turmas`).
  const turmasFiltradas = useMemo(() => {
    const termoTurma = filtros.turma.trim().toLowerCase();
    return turmas.filter((registro) => {
      if (termoTurma && !String(registro.nome_turma || '').toLowerCase().includes(termoTurma)) return false;
      if (filtros.modalidade && registro.nome_modalidade !== filtros.modalidade) return false;
      if (filtros.equipe && registro.nome_equipe !== filtros.equipe) return false;
      if (filtros.situacao === 'INCLUIDA' && !registro.ja_materializada) return false;
      if (filtros.situacao === 'DISPONIVEL' && registro.ja_materializada) return false;
      if (filtros.inconsistencias === 'COM' && !(registro.quantidade_inconsistencias > 0)) return false;
      if (filtros.inconsistencias === 'SEM' && registro.quantidade_inconsistencias > 0) return false;
      return true;
    });
  }, [turmas, filtros]);

  const columns = [
    {
      title: 'Turma',
      dataIndex: 'nome_turma',
      key: 'nome_turma',
      sorter: (a, b) => String(a?.nome_turma || '').localeCompare(String(b?.nome_turma || '')),
    },
    {
      title: 'Modalidade',
      dataIndex: 'nome_modalidade',
      key: 'nome_modalidade',
      sorter: (a, b) => String(a?.nome_modalidade || '').localeCompare(String(b?.nome_modalidade || '')),
    },
    {
      title: 'Unidade',
      dataIndex: 'nome_unidade',
      key: 'nome_unidade',
      sorter: (a, b) => String(a?.nome_unidade || '').localeCompare(String(b?.nome_unidade || '')),
    },
    {
      title: 'Professor',
      dataIndex: 'nome_equipe',
      key: 'nome_equipe',
      sorter: (a, b) => String(a?.nome_equipe || '').localeCompare(String(b?.nome_equipe || '')),
    },
    {
      title: 'Elegíveis',
      dataIndex: 'quantidade_elegiveis',
      key: 'quantidade_elegiveis',
      width: 100,
      sorter: (a, b) => (Number(a?.quantidade_elegiveis) || 0) - (Number(b?.quantidade_elegiveis) || 0),
    },
    {
      title: 'Inconsistências',
      dataIndex: 'quantidade_inconsistencias',
      key: 'quantidade_inconsistencias',
      width: 130,
      sorter: (a, b) => (Number(a?.quantidade_inconsistencias) || 0) - (Number(b?.quantidade_inconsistencias) || 0),
      render: (valor) =>
        valor > 0 ? (
          <Tag color="warning">{valor} inconsistência{valor > 1 ? 's' : ''}</Tag>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Situação',
      dataIndex: 'ja_materializada',
      key: 'ja_materializada',
      width: 200,
      sorter: (a, b) => Number(!!a?.ja_materializada) - Number(!!b?.ja_materializada),
      render: (jaMaterializada, registro) =>
        jaMaterializada ? (
          <Tag color="green">Incluída</Tag>
        ) : periodoAberto ? (
          <Button
            size="small"
            type="primary"
            ghost
            loading={incluindoIds.has(registro.id_turma)}
            onClick={() => adicionarUma(registro)}
          >
            Adicionar ao período
          </Button>
        ) : (
          <Text type="secondary">Não incluída</Text>
        ),
    },
  ];

  return (
    <div>
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={6}>
          <Input
            placeholder="Buscar turma"
            prefix={<SearchOutlined />}
            value={filtros.turma}
            onChange={(e) => handleFiltroChange('turma', e.target.value)}
            allowClear
          />
        </Col>
        <Col xs={24} sm={12} md={5}>
          <Select
            placeholder="Todas as modalidades"
            value={filtros.modalidade}
            onChange={(valor) => handleFiltroChange('modalidade', valor ?? null)}
            allowClear
            showSearch
            style={{ width: '100%' }}
            options={opcoesModalidade.map((nome) => ({ value: nome, label: nome }))}
          />
        </Col>
        <Col xs={24} sm={12} md={5}>
          <Select
            placeholder="Todos os professores"
            value={filtros.equipe}
            onChange={(valor) => handleFiltroChange('equipe', valor ?? null)}
            allowClear
            showSearch
            style={{ width: '100%' }}
            options={opcoesProfessor.map((nome) => ({ value: nome, label: nome }))}
          />
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Select
            placeholder="Todas as situações"
            value={filtros.situacao}
            onChange={(valor) => handleFiltroChange('situacao', valor ?? null)}
            allowClear
            style={{ width: '100%' }}
            options={[
              { value: 'INCLUIDA', label: 'Incluída' },
              { value: 'DISPONIVEL', label: 'Disponível para adicionar' },
            ]}
          />
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Select
            placeholder="Todas as inconsistências"
            value={filtros.inconsistencias}
            onChange={(valor) => handleFiltroChange('inconsistencias', valor ?? null)}
            allowClear
            style={{ width: '100%' }}
            options={[
              { value: 'COM', label: 'Com inconsistências' },
              { value: 'SEM', label: 'Sem inconsistências' },
            ]}
          />
        </Col>
        <Col xs={24} sm={12} md={4} lg={2}>
          <Button icon={<ClearOutlined />} onClick={limparFiltros} disabled={!filtroAtivo} block>
            Limpar
          </Button>
        </Col>
      </Row>

      {!periodoAberto && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="A inclusão de turmas só é permitida enquanto o período estiver Aberto."
        />
      )}

      {erro && (
        <Alert
          type="error"
          showIcon
          message="Não foi possível carregar"
          description={erro}
          style={{ marginBottom: 16 }}
          action={
            <Button size="small" icon={<ReloadOutlined />} onClick={carregar}>
              Tentar novamente
            </Button>
          }
        />
      )}

      {resultadoLote && (
        <Alert
          type={resultadoLote.falha > 0 ? 'warning' : 'success'}
          showIcon
          closable
          onClose={() => setResultadoLote(null)}
          style={{ marginBottom: 16 }}
          message={`${resultadoLote.sucesso} turma${resultadoLote.sucesso === 1 ? '' : 's'} adicionada${resultadoLote.sucesso === 1 ? '' : 's'}.${
            resultadoLote.falha > 0
              ? ` ${resultadoLote.falha} turma${resultadoLote.falha === 1 ? '' : 's'} não p${resultadoLote.falha === 1 ? 'ô' : 'u'}de${resultadoLote.falha === 1 ? '' : 'ram'} ser adicionada${resultadoLote.falha === 1 ? '' : 's'}.`
              : ''
          }`}
          description={
            resultadoLote.itensFalha.length > 0 ? (
              <List
                size="small"
                dataSource={resultadoLote.itensFalha}
                renderItem={(item) => (
                  <List.Item>
                    <Text strong>{item.nome}:</Text> <Text>{item.mensagem}</Text>
                  </List.Item>
                )}
              />
            ) : undefined
          }
        />
      )}

      {periodoAberto && (
        <div style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            disabled={selecionadas.length === 0}
            loading={incluindoLote}
            onClick={adicionarSelecionadas}
          >
            Adicionar turmas selecionadas {selecionadas.length > 0 ? `(${selecionadas.length})` : ''}
          </Button>
        </div>
      )}

      <Table
        rowKey="id_turma"
        columns={columns}
        dataSource={erro ? [] : turmasFiltradas}
        loading={loading}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        locale={{ emptyText: erro ? ' ' : (turmas.length > 0 ? 'Nenhuma turma corresponde aos filtros.' : 'Nenhuma turma candidata encontrada.') }}
        scroll={{ x: 1100 }}
        rowSelection={
          periodoAberto
            ? {
                selectedRowKeys: selecionadas,
                onChange: setSelecionadas,
                getCheckboxProps: (registro) => ({ disabled: registro.ja_materializada }),
              }
            : undefined
        }
      />
    </div>
  );
};

export default PeriodoTurmasPainel;
